import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, X } from "lucide-react";

const INTRO_MP4 = "/media/intro.mp4?v=2";
const INTRO_WEBM = "/media/intro.webm?v=2";
const POSTER_URL = "/media/intro-poster.jpg?v=2";
const SEEN_KEY = "fv_intro_seen";
const FADE_MS = 450;

const useBodyScrollLock = (locked) => {
  useEffect(() => {
    if (!locked) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [locked]);
};

const IntroSources = () => (
  <>
    <source src={INTRO_MP4} type="video/mp4" />
    <source src={INTRO_WEBM} type="video/webm" />
  </>
);

const IntroControls = ({ muted, onToggleSound, onSkip }) => (
  <>
    <div className="absolute top-8 left-8 hidden sm:flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-white/70 pointer-events-none">
      <span className="live-dot" /> Fedevo 2.0 · Urban Media
    </div>
    <div className="absolute top-5 right-5 sm:top-8 sm:right-8 flex items-center gap-2">
      <button type="button" onClick={onToggleSound} data-testid="intro-sound" aria-label={muted ? "Activer le son" : "Couper le son"}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white text-[10px] uppercase tracking-widest hover:bg-[color:var(--accent)] hover:text-black hover:border-[color:var(--accent)] transition-colors">
        {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{muted ? "Activer le son" : "Couper le son"}</span>
      </button>
      <button type="button" onClick={onSkip} data-testid="intro-skip"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--accent)] text-black font-semibold text-[10px] uppercase tracking-widest hover:brightness-110 transition">
        Passer l'intro <X className="w-3.5 h-3.5" />
      </button>
    </div>
  </>
);

/** Full-screen brand intro shown once per session on the home page. */
export const IntroSplash = () => {
  const [visible, setVisible] = useState(() => !sessionStorage.getItem(SEEN_KEY));
  const [closing, setClosing] = useState(false);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);
  useBodyScrollLock(visible);

  const close = (markSeen) => {
    if (closing) return;
    if (markSeen) sessionStorage.setItem(SEEN_KEY, "1");
    setClosing(true);
    setTimeout(() => setVisible(false), FADE_MS);
  };

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (v.paused) v.play().catch(() => {});
  };

  if (!visible) return null;

  return (
    <div className={`fixed inset-0 z-[100] bg-black overflow-hidden flex items-center justify-center transition-opacity duration-500 ${closing ? "opacity-0" : "opacity-100"}`} data-testid="intro-splash" role="dialog" aria-label="Intro Fedevo 2.0">
      <video muted autoPlay loop playsInline aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30 blur-2xl scale-110 pointer-events-none">
        <IntroSources />
      </video>
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80 pointer-events-none" />

      <div className="relative flex flex-col items-stretch gap-4 w-fit max-w-[92vw]">
        <video
          ref={videoRef}
          data-testid="intro-video"
          poster={POSTER_URL}
          autoPlay
          muted
          playsInline
          preload="auto"
          onTimeUpdate={(e) => { const el = e.currentTarget; if (el.duration) setProgress(el.currentTime / el.duration); }}
          onEnded={() => close(true)}
          onError={(e) => { if (e.currentTarget.error) close(false); }}
          style={{ height: "min(calc(100dvh - 8rem), calc(92vw * 16 / 9), 900px)", aspectRatio: "9 / 16" }}
          className="w-auto object-cover rounded-2xl shadow-[0_40px_120px_-30px_rgba(240,180,41,0.45)] ring-1 ring-white/10 bg-black"
        >
          <source src={INTRO_MP4} type="video/mp4" />
          <source src={INTRO_WEBM} type="video/webm" onError={() => close(false)} />
        </video>
        <div className="h-1 rounded-full bg-white/15 overflow-hidden" data-testid="intro-progress">
          <div className="h-full bg-[color:var(--accent)] transition-[width] duration-200" style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      </div>

      <IntroControls muted={muted} onToggleSound={toggleSound} onSkip={() => close(true)} />
    </div>
  );
};
